import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, LogOut, Menu, X, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const getInitials = (name = '') =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

/**
 * Admin console shell: dark sidebar with section nav + footer (theme toggle,
 * account, logout), and a responsive drawer on narrow screens.
 */
function AdminLayout({ nav, activeId, onSelect, theme, onToggleTheme, title, subtitle, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);

  const handleSelect = (id) => {
    onSelect(id);
    setNavOpen(false);
  };

  return (
    <div className={`admin-shell ${navOpen ? 'nav-open' : ''}`} data-theme={theme}>
      <div className="admin-scrim" onClick={() => setNavOpen(false)} />

      <aside className="admin-sidebar">
        <div className="admin-brand">
          <span className="admin-brand-mark">R</span>
          RGNR Admin
        </div>

        <nav className="admin-nav">
          <span className="admin-nav-label">Manage</span>
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={`admin-nav-item ${activeId === item.id ? 'active' : ''}`}
                onClick={() => handleSelect(item.id)}
              >
                <Icon size={18} className="nav-icon" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          <button className="admin-foot-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={18} />
            Back to site
          </button>
          <button className="admin-theme-toggle" onClick={onToggleTheme}>
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          <div className="admin-account">
            <div className="admin-account-avatar">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} />
              ) : (
                <span>{getInitials(user?.name)}</span>
              )}
            </div>
            <div className="admin-account-info">
              <span className="admin-account-name">{user?.name}</span>
              <span className="admin-account-role">Fund admin</span>
            </div>
          </div>
          <button className="admin-foot-btn" onClick={() => logout()}>
            <LogOut size={18} />
            Log out
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <div className="admin-mobile-topbar">
          <button
            className="admin-hamburger"
            onClick={() => setNavOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            {navOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <strong>{title}</strong>
        </div>

        <div className="admin-content">
          <div className="admin-page-head">
            <h1 className="admin-page-title">{title}</h1>
            {subtitle && <p className="admin-page-sub">{subtitle}</p>}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;
